import * as XLSX from 'xlsx';
import { OperationTimeRecord } from '../../../shared/services/operationTimes/types';
import { formatDateTime, formatDuration } from '../../../shared/utils/datetime';

/**
 * Exportacion de Consolidado (Excel / CSV / Excel + tabla dinamica).
 * INDEPENDIENTE de cualquier exportacion existente en la Suite - vive solo
 * dentro de este modulo.
 */

function novedadLabel(r: OperationTimeRecord): string {
  if (r.novedad === 'none') return '—';
  return r.novedad === 'deleted' ? 'Borrado' : 'Modificado';
}

/** Aplana los registros a filas planas con encabezados en español, listas para Excel/CSV. */
function toExportRows(records: OperationTimeRecord[]) {
  return records.map((r) => ({
    Código: r.code,
    Usuario: r.userName,
    Actividad: r.activityName,
    Inicio: formatDateTime(r.startDateTime),
    Fin: formatDateTime(r.endDateTime),
    'Duración (seg)': r.durationSeconds,
    Duración: formatDuration(r.durationSeconds),
    Observaciones: r.observations || '—',
    'Modificado / Borrado': novedadLabel(r),
    Motivo: r.novedadReason || '—',
  }));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function dateStamp() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

/** Descarga un .xlsx con una sola hoja: el detalle completo de registros. */
export function exportExcel(records: OperationTimeRecord[]) {
  const sheet = XLSX.utils.json_to_sheet(toExportRows(records));
  sheet['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 12 }, { wch: 30 }, { wch: 16 }, { wch: 30 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Consolidado');
  XLSX.writeFile(wb, `consolidado_tiempos_${dateStamp()}.xlsx`);
}

/** Descarga un .csv con el mismo detalle (para importar en cualquier otra herramienta). */
export function exportCsv(records: OperationTimeRecord[]) {
  const sheet = XLSX.utils.json_to_sheet(toExportRows(records));
  const csv = XLSX.utils.sheet_to_csv(sheet);
  // BOM para que Excel abra los tildes/eñes correctamente al abrir el CSV directo.
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `consolidado_tiempos_${dateStamp()}.csv`);
}

/**
 * Descarga un .xlsx con DOS hojas:
 *  - "Detalle" (Hoja 1): el mismo detalle plano de exportExcel.
 *  - "Tabla dinámica" (Hoja 2): un CRUCE Usuario x Actividad con el total de
 *    horas trabajadas por cada combinación, mas fila y columna de totales.
 *
 * NOTA para quien lo reciba: esto es un MODELO de tabla dinamica ya calculado
 * (un cruce de datos armado con formulas resueltas), no una Tabla Dinamica
 * nativa e interactiva de Excel - el motor gratuito que usamos para generar
 * el archivo (SheetJS Community) no puede escribir el objeto de PivotTable
 * real de Excel. Si se necesita una 100% interactiva (arrastrar y soltar
 * campos), en Excel: seleccionar el rango de "Detalle" -> Insertar -> Tabla
 * dinámica.
 */
export function exportExcelWithPivot(records: OperationTimeRecord[]) {
  const detailSheet = XLSX.utils.json_to_sheet(toExportRows(records));
  detailSheet['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 12 }, { wch: 30 }, { wch: 16 }, { wch: 30 }];

  const users = Array.from(new Set(records.map((r) => r.userName))).sort();
  const activities = Array.from(new Set(records.map((r) => r.activityName))).sort();

  // Suma de segundos por combinacion Usuario/Actividad.
  const totals = new Map<string, number>();
  for (const r of records) {
    const key = `${r.userName}|||${r.activityName}`;
    totals.set(key, (totals.get(key) ?? 0) + r.durationSeconds);
  }

  const header = ['Usuario', ...activities, 'Total general'];
  const rows: (string | number)[][] = [header];

  for (const user of users) {
    const row: (string | number)[] = [user];
    let userTotal = 0;
    for (const activity of activities) {
      const seconds = totals.get(`${user}|||${activity}`) ?? 0;
      userTotal += seconds;
      row.push(seconds > 0 ? formatDuration(seconds) : '—');
    }
    row.push(formatDuration(userTotal));
    rows.push(row);
  }

  // Fila de totales por actividad, al final.
  const totalRow: (string | number)[] = ['Total general'];
  let grandTotal = 0;
  for (const activity of activities) {
    const activityTotal = records.filter((r) => r.activityName === activity).reduce((sum, r) => sum + r.durationSeconds, 0);
    grandTotal += activityTotal;
    totalRow.push(formatDuration(activityTotal));
  }
  totalRow.push(formatDuration(grandTotal));
  rows.push(totalRow);

  const pivotSheet = XLSX.utils.aoa_to_sheet(rows);
  pivotSheet['!cols'] = [{ wch: 22 }, ...activities.map(() => ({ wch: 16 })), { wch: 16 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, detailSheet, 'Detalle');
  XLSX.utils.book_append_sheet(wb, pivotSheet, 'Tabla dinámica');
  XLSX.writeFile(wb, `consolidado_tiempos_tabla_dinamica_${dateStamp()}.xlsx`);
}
